/**
 * Data processor for the `wsolution_logs` Elasticsearch config query.
 *
 * Shapes each ES hit (`contents[]`) into a flat, render-ready `rows` array so
 * the WSolution logs page renders 100% declaratively in YAML — no presentational
 * React component required. Everything the YAML can't express (date / byte /
 * duration formatting, HTTP method & status colour classes, URL splitting,
 * header/body JSON parsing for the detail pop-in, and the webservice label) is
 * baked here. The rest of the unified envelope (pagination, sort, aggregations)
 * is left untouched so the faceted-search kit primitives can read it directly.
 *
 * Runs for both the initial GET and every POST refetch — matched by URL.
 */

import { wsidHost, wsolutionHrefs, wsolutionParams } from './wsolutionWsid';

const METHOD_COLORS = {
    GET:     'bg-blue-100 text-blue-700',
    POST:    'bg-green-100 text-green-700',
    PUT:     'bg-orange-100 text-orange-700',
    PATCH:   'bg-purple-100 text-purple-700',
    DELETE:  'bg-red-100 text-red-700',
    HEAD:    'bg-gray-100 text-gray-600',
    OPTIONS: 'bg-gray-100 text-gray-600',
};
const HTTP_STATUS_COLORS = {
    2: 'bg-green-100 text-green-700',
    3: 'bg-blue-100 text-blue-700',
    4: 'bg-orange-100 text-orange-700',
    5: 'bg-red-100 text-red-700',
};
const BADGE = 'inline-flex px-2 py-0.5 rounded text-xs font-semibold';

function methodColorClass(method) {
    return `${BADGE} uppercase ${METHOD_COLORS[String(method).toUpperCase()] ?? 'bg-gray-100 text-gray-600'}`;
}

function httpStatusColorClass(code) {
    const family = Math.floor(Number(code) / 100);
    return `${BADGE} ${HTTP_STATUS_COLORS[family] ?? 'bg-gray-100 text-gray-600'}`;
}

function formatDate(value) {
    if (!value) return null;
    try {
        return new Intl.DateTimeFormat('fr-FR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
        }).format(new Date(value));
    } catch {
        return String(value);
    }
}

function getRowDate(row) {
    return row.destination_response_start_time
        ?? row.destination_response_stop_time
        ?? row['@timestamp'] ?? row.record_date ?? row.passeplat_date ?? row.created_at ?? row.date ?? null;
}

/** Formats a byte count into o / Ko / Mo (response weight). */
function formatBytes(n) {
    const b = Number(n);
    if (n == null || !Number.isFinite(b)) return null;
    if (b < 1024) return `${b} o`;
    if (b < 1024 * 1024) return `${(b / 1024).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Ko`;
    return `${(b / (1024 * 1024)).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Mo`;
}

/**
 * Time the application itself spent processing the request, in ms : total app
 * execution minus the destination round-trip. Durations are stored in
 * microseconds. Clamped to ≥ 0.
 */
function appProcessingMs(row) {
    const total = Number(row.app_execution_until_stop_duration);
    if (!Number.isFinite(total)) return null;
    const dest = Number(row.destination_round_trip_duration);
    const overhead = Number.isFinite(dest) ? total - dest : total;
    return Math.max(0, Math.round(overhead / 1000));
}

/**
 * Normalises a stored headers field (JSON string of `[{key, value}]`) into a
 * plain `{name: value}` object. Duplicate names collapse to an array. Returns
 * the raw value on parse failure, or null when empty.
 */
function headerObject(raw) {
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            const obj = {};
            for (const h of parsed) {
                if (h && h.key != null) {
                    obj[h.key] = obj[h.key] === undefined ? h.value : [].concat(obj[h.key], h.value);
                }
            }
            return obj;
        }
        return parsed && typeof parsed === 'object' ? parsed : (raw || null);
    } catch {
        return raw || null;
    }
}

/** Parses a value as JSON when possible (object/array), else returns it raw. */
function maybeJson(raw) {
    if (raw == null || raw === '') return null;
    if (typeof raw === 'object') return raw;
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : raw;
    } catch {
        return raw;
    }
}

/** Splits a URL into { origin, path } for styled display. Falls back gracefully. */
function parseUrlParts(raw) {
    if (!raw) return { origin: '', path: '' };
    try {
        const u = new URL(String(raw));
        return { origin: u.origin, path: u.pathname + u.search + u.hash };
    } catch {
        const slashIdx = String(raw).indexOf('/', 8);
        if (slashIdx === -1) return { origin: String(raw), path: '' };
        return { origin: String(raw).slice(0, slashIdx), path: String(raw).slice(slashIdx) };
    }
}

export const wsolutionLogsProcessor = ({ requestContext, responseContext, dataToProcess }) => {
    // Deux écrans, un seul formatage : /api-keys/logs sert les MÊMES documents
    // `analyzable_content` (les appels par clé d'API y sont écrits par le même
    // pipeline PassePlat), avec les mêmes colonnes. Élargir ce garde vaut mieux
    // que dupliquer 200 lignes de mise en forme.
    const url = requestContext?.url ?? '';
    if (!url.includes('/config/wsolution_logs') && !url.includes('/config/api_key_calls')) return dataToProcess;
    if (responseContext?.status >= 400) return dataToProcess;
    if (!dataToProcess || typeof dataToProcess !== 'object') return dataToProcess;

    const contents = Array.isArray(dataToProcess.contents) ? dataToProcess.contents : [];

    const rows = contents.map((row) => {
        const { origin, path } = parseUrlParts(row.destination_url);
        const weight = formatBytes(row.destination_response_body_length);
        const appMs = appProcessingMs(row);
        const date = formatDate(getRowDate(row));
        const method = row.http_method ?? '';
        const hasMethod = !!method;
        const code = row.destination_response_http_status_code;
        const hasCode = code != null;
        const url = row.destination_url ? String(row.destination_url) : '';
        // Qui a passé l'appel : sur un webservice ENTRANT c'est l'adresse du
        // client (la passerelle la résout depuis les en-têtes de proxy), sur un
        // sortant celle qui a déclenché le traitement côté site. Le référent
        // n'est renseigné que dans le sens entrant, et seulement si l'appelant
        // l'annonce — d'où une liste de présence plutôt qu'un tiret de plus.
        const caller = row.initiator_addr ? String(row.initiator_addr) : '';
        const referer = row.initiator_referer ? String(row.initiator_referer) : '';

        return {
            // Date — gray dash placeholder when missing (single span, no YAML branch).
            date: date ?? '—',
            // URL parts (native title tooltip carries the full URL).
            url,
            urlOrigin: origin,
            urlPath: url ? (path || '/') : '—',
            caller: caller || '—',
            refererRows: referer ? [{ referer }] : [],
            // Method / code: when present a coloured badge class, when absent a
            // plain gray dash — class + content both come from here so the YAML
            // renders one unconditional span.
            method: hasMethod ? method : '—',
            methodClass: hasMethod ? methodColorClass(method) : 'text-gray-300',
            code: hasCode ? String(code) : '—',
            codeClass: hasCode ? httpStatusColorClass(code) : 'text-gray-300',
            weight: weight ?? '—',
            appMs: appMs != null ? `${appMs.toLocaleString('fr-FR')} ms` : '—',
            // Pre-parsed payload for the detail pop-in.
            detail: {
                method: hasMethod ? method : '',
                methodClass: methodColorClass(method),
                hasMethod,
                code: hasCode ? String(code) : '',
                codeClass: httpStatusColorClass(code),
                hasCode,
                url,
                caller: caller || '—',
                referer: referer || '—',
                reqHeaders: headerObject(row.initiator_request_headers),
                resHeaders: headerObject(row.destination_response_headers),
                reqBody: maybeJson(row.initiator_request_body),
                resBody: maybeJson(row.destination_response_body),
            },
        };
    });

    const wsid = new URLSearchParams(window.location.search).get('wsid') ?? '';

    return {
        ...dataToProcess,
        rows,
        view: {
            wsLabel: wsidHost(wsid) || wsid,
            // Calculés ici plutôt qu'en gabarit PathLink (cf. wsolutionHrefs) : le
            // tableau de bord AVEC le préfiltre courant, et ces logs SANS.
            dashboardHref: wsolutionHrefs(wsolutionParams(window.location.search)).dashboardHref,
            unfilteredHref: wsolutionHrefs({ id: wsolutionParams(window.location.search).id, wsid }).logsHref,
        },
    };
};
